// i18n with a 5-tier resolution chain:
//
//   1. Server (signed-in user's identity.users.locale)   — authoritative
//      for FUTURE emails; cookie can override if newer.
//   2. `nexus_locale` cookie at Domain=.nexusdijital.com  — shared with
//      the landing page (last explicit pick across our properties).
//   3. localStorage (per-subdomain fallback)             — sticky
//   4. /v1/geo (Cloudflare CF-IPCountry -> tr|en)        — first paint hint
//   5. navigator.language                                — last resort
//
// On boot we synchronously pick the best of (2), (3), (5), so the UI
// never flickers. Tiers (1) and (4) come in async and only override if
// the user hasn't made an explicit pick yet.
//
// When the user clicks the locale toggle:
//   - update React state immediately (zero-latency UI flip)
//   - write `nexus_locale` cookie at Domain=.nexusdijital.com so the
//     landing page picks it up on next visit
//   - write localStorage for fast same-subdomain reads
//   - if signed in, fire PATCH /v1/auth/me {locale} so notif-service
//     uses the matching template for transactional + marketing emails

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { auth, geo } from "./api";
import { readCookie, writeSharedCookie } from "./cookie";

const SHARED_COOKIE = "nexus_locale";

export type Locale = "tr" | "en";

const STORAGE_KEY = "nexus.locale";
// Why the current locale was set. Used so async sources (server, geo) only
// override when the user hasn't made an explicit pick yet.
type Source = "boot" | "user" | "server";

type Dict = Record<string, string>;

const tr: Dict = {
  "brand.name": "Nexus Dijital",
  "common.continue": "Devam et",
  "common.back": "Geri",
  "common.signOut": "Çıkış yap",
  "common.loading": "Yükleniyor...",
  "common.errorGeneric": "Bir şeyler ters gitti. Lütfen tekrar deneyin.",

  "auth.login.title": "Hoş geldiniz",
  "auth.login.subtitle": "Hesabınıza giriş yapın.",
  "auth.login.email": "E-posta",
  "auth.login.password": "Şifre",
  "auth.login.submit": "Giriş yap",
  "auth.login.toSignup": "Hesabınız yok mu? Kaydolun.",
  "auth.login.forgot": "Şifrenizi mi unuttunuz?",

  // /verify-email
  "verifyEmail.verifying": "E-postanız doğrulanıyor…",
  "verifyEmail.verifyingHint": "Birkaç saniye sürebilir.",
  "verifyEmail.verified": "E-posta doğrulandı",
  "verifyEmail.verifiedHint": "{email} adresi onaylandı. Artık giriş yapabilirsiniz.",
  "verifyEmail.continueToLogin": "Giriş yap",
  "verifyEmail.cannotVerify": "Doğrulama tamamlanamadı",
  "verifyEmail.missingToken": "Bağlantı eksik veya geçersiz. Lütfen e-postanızdaki bağlantıyı yeniden kullanın.",
  "verifyEmail.mismatch": "E-posta adresiniz bu bağlantı verildikten sonra değişti. Yeni bir doğrulama isteyin.",
  "verifyEmail.expired": "Bağlantının süresi dolmuş veya daha önce kullanılmış. Yeni bir doğrulama isteyin.",
  "verifyEmail.backToLogin": "Girişe dön",

  // /forgot-password
  "forgotPassword.title": "Şifrenizi mi unuttunuz?",
  "forgotPassword.subtitle": "E-posta adresinizi girin, sıfırlama bağlantısı göndereceğiz.",
  "forgotPassword.email": "E-posta",
  "forgotPassword.sendLink": "Sıfırlama bağlantısı gönder",
  "forgotPassword.sentTitle": "Bağlantı yola çıktı",
  "forgotPassword.sentBody": "Bu adres kayıtlıysa birkaç dakika içinde bir e-posta alacaksınız. Spam klasörünü kontrol etmeyi unutmayın.",
  "forgotPassword.backToLogin": "Girişe dön",

  // /reset-password
  "resetPassword.title": "Yeni şifre belirleyin",
  "resetPassword.subtitle": "Yeni şifrenizi iki kez girin. Tüm aktif oturumlarınız kapatılacak.",
  "resetPassword.newPassword": "Yeni şifre",
  "resetPassword.confirm": "Yeni şifreyi doğrulayın",
  "resetPassword.submit": "Şifreyi güncelle",
  "resetPassword.tooShort": "Şifre en az 8 karakter olmalı",
  "resetPassword.mismatch": "Şifreler eşleşmiyor",
  "resetPassword.expired": "Sıfırlama bağlantısının süresi dolmuş veya daha önce kullanılmış. Yeni bir bağlantı isteyin.",
  "resetPassword.missingToken": "Geçersiz bağlantı",
  "resetPassword.missingTokenBody": "Bu bağlantı eksik ya da bozuk. Lütfen yeni bir sıfırlama bağlantısı isteyin.",
  "resetPassword.requestNew": "Yeni bağlantı iste",
  "resetPassword.revokesAllSessions": "Başarılı sıfırlama tüm cihazlardaki oturumlarınızı kapatır.",

  "auth.login.badCredentials": "E-posta veya şifre hatalı.",

  "auth.signup.title": "Hesap oluşturun",
  "auth.signup.subtitle": "Birkaç saniyede başlayın.",
  "auth.signup.displayName": "Ad Soyad",
  "auth.signup.email": "E-posta",
  "auth.signup.password": "Şifre",
  "auth.signup.passwordHint": "En az 8 karakter. Büyük/küçük harf + rakam + sembol karışımı önerilir.",
  "auth.signup.submit": "Hesap oluştur",
  "auth.signup.toLogin": "Zaten bir hesabınız var mı? Giriş yapın.",
  "auth.signup.emailTaken": "Bu e-posta zaten kayıtlı.",
  "auth.signup.passwordShort": "Şifre en az 8 karakter olmalı.",
  "auth.signup.confirmPassword": "Şifreyi doğrula",
  "auth.signup.passwordMismatch": "Şifreler eşleşmiyor.",
  "auth.signup.strength.weak": "Zayıf",
  "auth.signup.strength.fair": "Orta",
  "auth.signup.strength.good": "İyi",
  "auth.signup.strength.strong": "Güçlü",
  "auth.signup.displayNameRequired": "Ad Soyad gerekli.",

  "onboarding.title": "İlk çalışma alanınızı oluşturun",
  "onboarding.subtitle": "Çalışma alanı; markalarınız, içeriğiniz ve ekibinizin kapsayıcısıdır.",
  "onboarding.name": "Çalışma alanı adı",
  "onboarding.slug": "URL kısayolu",
  "onboarding.slugHint": "küçük harf, rakam ve tire. 2–32 karakter.",
  "onboarding.submit": "Çalışma alanını oluştur",
  "onboarding.slugTaken": "Bu URL kısayolu zaten alınmış.",

  "app.title": "Kontrol paneli",
  "app.welcome": "Hoş geldiniz, {name}.",
  "app.placeholder": "Markalar, içerik ve analizler buraya yakında geliyor.",
  "app.workspaceLabel": "Çalışma alanı",
  "app.home": "Anasayfa",
  "app.settings": "Ayarlar",

  "notfound.eyebrow": "SAYFA BULUNAMADI · 404",
  "notfound.title": "Aradığınız sayfayı bulamadık.",
  "notfound.subtitle": "Bağlantı yanlış olabilir ya da sayfa taşınmış olabilir.",
  "notfound.cta": "Ana sayfaya dön",

  "error.eyebrow": "BİR HATA OLUŞTU · ERROR",
  "error.title": "Bir şeyler ters gitti.",
  "error.subtitle": "Bir aksaklık oldu. Tekrar dener misiniz?",
  "error.refresh": "Tekrar dene",
  "error.toHome": "Ana sayfaya dön",

  "settings.title": "Hesap ayarları",
  "settings.profile": "Profil",
  "settings.email": "E-posta",
  "settings.emailNote": "E-postanızı değiştirmek için destek ekibiyle iletişime geçin.",
  "settings.displayName": "Ad Soyad",
  "settings.locale": "Dil",
  "settings.save": "Kaydet",
  "settings.saved": "Kaydedildi.",
  "settings.workspace": "Çalışma alanı",
  "settings.workspaceSlug": "URL kısayolu",
  "settings.workspaceName": "Çalışma alanı adı",
  "settings.workspaceTimezone": "Saat dilimi",
  "settings.workspaceLocale": "Çalışma alanı dili",
  "settings.workspaceEditHint":
    "Yalnızca sahip veya yönetici düzenleyebilir. URL kısayolu kalıcıdır.",
  "settings.workspaceUpdated": "Çalışma alanı güncellendi.",
  "settings.members": "Üyeler",
  "settings.membersSubtitle":
    "Bu çalışma alanına erişimi olan kullanıcılar. Yeni üye eklemek için önce hesabın kayıtlı olması gerekir.",
  "settings.membersEmpty": "Henüz üye yok.",
  "settings.addMember": "Üye ekle",
  "settings.addMemberEmail": "Kullanıcı e-postası",
  "settings.addMemberRole": "Rol",
  "settings.addMemberCta": "Ekle",
  "settings.memberRole.owner": "Sahip",
  "settings.memberRole.admin": "Yönetici",
  "settings.memberRole.editor": "Editör",
  "settings.memberRole.approver": "Onaylayıcı",
  "settings.memberRole.viewer": "İzleyici",
  "settings.memberSelf": "(siz)",
  "settings.memberRemove": "Çıkar",
  "settings.memberLeave": "Çalışma alanından ayrıl",
  "settings.memberRemoveConfirm": "Bu üyeyi gerçekten çıkarmak istiyor musunuz?",
  "settings.memberLeaveConfirm":
    "Bu çalışma alanından ayrıldığınızda artık verilere erişemezsiniz. Devam edilsin mi?",
  "settings.error.notFoundEmail": "Bu e-postaya sahip bir kullanıcı bulunamadı.",
  "settings.error.alreadyMember": "Bu kullanıcı zaten üye.",
  "settings.error.forbidden": "Bu işlem için yetkiniz yok.",
  "settings.error.cannotRemoveOwner": "Sahip çıkarılamaz.",
  "settings.invite.sent": "{email} adresine davet gönderildi.",
  "settings.invite.alreadyPending": "{email} için zaten bekleyen bir davet var.",

  // /accept-invite
  "acceptInvite.accepting": "Davet kabul ediliyor…",
  "acceptInvite.acceptingHint": "Birkaç saniye sürebilir.",
  "acceptInvite.accepted": "Davet kabul edildi",
  "acceptInvite.acceptedHint": "{workspace} alanına eklendiniz. Devam ediliyor…",
  "acceptInvite.continue": "Devam et",
  "acceptInvite.expired": "Davet bağlantısı geçersiz",
  "acceptInvite.expiredHint": "Bağlantının süresi dolmuş veya daha önce kullanılmış. Davet edenden yeni bir bağlantı isteyin.",
  "acceptInvite.missingToken": "Geçersiz bağlantı",
  "acceptInvite.missingTokenHint": "Davet bağlantısı eksik veya bozuk. Davet edenden yeniden göndermesini isteyin.",
  "acceptInvite.backToLogin": "Girişe dön",
  "settings.signOut": "Hesaptan çıkış yap",

  "brands.title": "Markalar",
  "brands.eyebrow": "MARKA STÜDYOSU · BRAND STUDIO",
  "brands.empty.title": "Henüz marka eklenmedi",
  "brands.empty.subtitle": "İlk markanızı ekleyerek başlayın. Her marka kendi içerik takvimini, sesini ve sosyal hesaplarını taşır.",
  "brands.new": "Yeni marka",
  "brands.openDetail": "Detayı aç",
  "brands.create.title": "Yeni marka",
  "brands.create.subtitle": "Markanız hakkında temel bilgileri girin. Daha sonra düzenleyebilirsiniz.",
  "brands.field.legalName": "Yasal unvan",
  "brands.field.legalNameHint": "Tüzel kişi tam adı (örn. Mesh Yazılım Teknoloji Limited Şirketi).",
  "brands.field.tradeName": "Ticari ad",
  "brands.field.tradeNameHint": "Hedef kitlenizin bildiği ad.",
  "brands.field.slug": "URL kısayolu",
  "brands.field.slugHint": "küçük harf, rakam ve tire. 2–32 karakter, çalışma alanı içinde benzersiz.",
  "brands.field.country": "Ülke",
  "brands.field.primaryLanguage": "Birincil dil",
  "brands.field.timezone": "Saat dilimi",
  "brands.field.industry": "Sektör",
  "brands.field.industryHint": "İsteğe bağlı. Örn. Yazılım, Restoran, Moda.",
  "brands.field.category": "Kategori",
  "brands.field.categoryHint": "İsteğe bağlı. Daha dar sınıflandırma.",
  "brands.field.commentsMode": "Yorum modu",
  "brands.field.commentsMode.approval": "Onay gerektir",
  "brands.field.commentsMode.auto": "Otomatik yayınla",
  "brands.field.telegramChannel": "Telegram kanalı",
  "brands.field.telegramChannelHint": "Yayın yapılacak kanal (örn. @marka_haberleri). @nexus_dijital_bot'u kanala admin olarak ekleyin. Şimdilik tek desteklenen yayın kanalı.",
  "brands.field.aiDisclosure": "Yapay zekâ üretimi etiketi göster",
  "brands.field.aiDisclosureHint": "Yayınlanan içeriklerin AI yardımıyla hazırlandığını belirtir.",
  "brands.field.holidayPosts": "Tatil günleri için içerik öner",
  "brands.create.submit": "Markayı oluştur",
  "brands.slugTaken": "Bu URL kısayolu bu çalışma alanında zaten alınmış.",
  "brands.detail.summary": "Özet",
  "brands.detail.edit": "Düzenle",
  "brands.detail.cancel": "İptal",
  "brands.detail.save": "Değişiklikleri kaydet",
  "brands.detail.saved": "Kaydedildi.",
  "brands.detail.delete": "Markayı sil",
  "brands.detail.deleteConfirm.title": "Bu markayı silmek istediğinizden emin misiniz?",
  "brands.detail.deleteConfirm.body": "İlişkili tüm marka kiti, bilgi tabanı ve varlıklar erişilemez hale gelir. Bu işlem geri alınamaz.",
  "brands.detail.deleteConfirm.confirm": "Evet, sil",
  "brands.detail.deleteConfirm.cancel": "Vazgeç",
  "brands.detail.kit.title": "Marka kiti",
  "brands.detail.kit.empty": "Henüz kit oluşturulmadı.",
  "brands.detail.kb.title": "Bilgi tabanı",
  "brands.detail.kb.empty": "Henüz bilgi tabanı doldurulmadı.",
  "brands.detail.kb.status": "Durum",

  "brands.tabs.general": "Genel",
  "brands.tabs.voice": "Ses",
  "brands.tabs.audience": "Hedef kitle",
  "brands.tabs.products": "Ürünler",
  "brands.tabs.services": "Hizmetler",
  "brands.tabs.topics": "Konular",
  "brands.tabs.examples": "Örnekler",
  "brands.tabs.templates": "Şablonlar",
  "brands.tabs.integrations": "Entegrasyonlar",

  // Integrations panel — TR
  "brands.integrations.title": "Sosyal hesap bağlantıları",
  "brands.integrations.subtitle": "Marka adına yayın yapacağımız hesapları bağlayın. Test aşamasındaki Meta uygulamaları yalnızca tester olarak eklediğiniz hesaplara izin verir; tam yayın için App Review'dan geçmesi gerekir.",
  "brands.integrations.facebook": "Facebook Sayfa",
  "brands.integrations.instagram": "Instagram Business",
  "brands.integrations.linkedin": "LinkedIn",
  "brands.integrations.meta.title": "Facebook + Instagram",
  "brands.integrations.meta.subtitle": "Meta OAuth ile sayfa ve IG Business hesabınızı bağlayın. Test modunda yalnızca uygulamanızda tester olarak listelenen hesaplar bağlanabilir.",
  "brands.integrations.linkedin.title": "LinkedIn",
  "brands.integrations.linkedin.subtitle": "Kişisel LinkedIn profilinizde paylaşım yapın. Şirket Sayfası yönetimi MDP onayı bekleniyor (4-12+ hafta).",
  "brands.integrations.x.title": "X (Twitter)",
  "brands.integrations.x.subtitle": "X hesabınıza bağlanın ve paylaşımlarınızı buradan yönetin. Şu anda yalnızca metin gönderileri; görsel ekleme yakında.",
  "brands.integrations.x": "X",
  "brands.integrations.telegram.title": "Telegram Kanal",
  "brands.integrations.telegram.subtitle": "Markanız adına Telegram kanalına gönderi yayınlamak için aşağıdaki adımları izleyin. Tüm kiracılar Nexus'un paylaşımlı botunu kullanır — kanal sahibi olarak yapmanız gereken yalnızca botu yönetici olarak eklemek ve kanal kullanıcı adınızı buraya yapıştırmak.",
  "brands.integrations.telegram.step1.before": "Telegram'da ",
  "brands.integrations.telegram.step1.after": " botunu açın ve botla bir sohbet başlatın (Start tuşuna basın).",
  "brands.integrations.telegram.step2": "Telegram'da kanalınızı açın → kanal başlığına dokunun → Yöneticiler → Yönetici ekle.",
  "brands.integrations.telegram.step3": "Yukarıdaki bot kullanıcı adını arayın, seçin ve \"Gönderi Yayınlama\" yetkisini açık bırakın. Diğer yetkilere gerek yok.",
  "brands.integrations.telegram.step4": "Kanal kullanıcı adınızı (örn. @nexusdijital_haber) aşağıdaki kutuya yapıştırın ve Kaydet'e basın.",
  "brands.integrations.telegram.field": "Kanal kullanıcı adı veya sayısal ID",
  "brands.integrations.telegram.hint": "Genel kanallar için @kanaladi yazın; özel kanallar için sayısal sohbet ID (örn. -1001234567890).",
  "brands.integrations.telegram.save": "Kaydet",
  "brands.integrations.telegram.saved": "Telegram kanalı kaydedildi.",
  "brands.integrations.telegram.connected": "Bağlı: {channel}",
  "brands.audience.regionScope": "Arama bölgesi",
  "brands.audience.regionScope.worldwide": "Dünya geneli",
  "brands.audience.regionScopeHint": "Bölge aramasını bir ülkeye sınırla. Birden fazla pazarı hedefliyorsanız 'Dünya geneli' seçin.",
  "brands.integrations.connect": "Bağlan",
  "brands.integrations.disconnect": "Bağlantıyı kes",
  "brands.integrations.soon": "Yakında",
  "brands.integrations.connected": "Hesaplar başarıyla bağlandı.",
  "brands.integrations.meta.pickerTitle": "Bu markaya hangi sayfaları bağlayalım?",
  "brands.integrations.meta.pickerSubtitle": "Facebook hesabınızda yönetici olduğunuz tüm sayfalar aşağıda listeleniyor. Sadece bu markaya ait olan sayfa(ları) seçin — diğer markalarınızın sayfalarını işaretlemeyin.",
  "brands.integrations.meta.pickerNoIG": "Bu sayfaya bağlı Instagram Business hesabı yok",
  "brands.integrations.meta.pickerNoPages": "Bağlanabilecek sayfa bulunamadı.",
  "brands.integrations.meta.pickerPickAtLeastOne": "En az bir sayfa seçin.",
  "brands.integrations.meta.pickerSelectedCount": "{n} sayfa seçildi",
  "brands.integrations.meta.pickerConfirm": "Seçileni bağla",
  "brands.integrations.connected.title": "Bağlı hesaplar",
  "brands.integrations.connected.empty": "Henüz bağlı hesap yok.",
  "brands.integrations.error.exchange_failed": "Meta token alışverişi başarısız oldu — App ID/Secret veya callback URL'sini kontrol edin.",
  "brands.integrations.error.list_pages_failed": "Sayfalarınız listelenemedi. Yeniden deneyin.",
  "brands.integrations.error.no_pages": "Hesabınızda yönettiğiniz bir Facebook Sayfası bulunamadı.",
  "brands.tabs.kit": "Marka kiti",
  "brands.tabs.knowledge": "Bilgi tabanı",

  // Templates panel — TR
  "brands.templates.title": "Gönderi şablonları",
  "brands.templates.subtitle": "Tekrarlayan paylaşım iskeletleri. AI içerik üretirken eşleşen şablonu seçer; pazartesi motivasyonu, haftalık ürün öne çıkarma gibi seriler her hafta aynı hissi verir.",
  "brands.templates.new": "Yeni şablon",
  "brands.templates.empty": "Henüz şablon yok. İlk şablonu oluşturmak için \"Yeni şablon\".",
  "brands.templates.edit": "Düzenle",
  "brands.templates.delete": "Sil",
  "brands.templates.paused": "Duraklatıldı",
  "brands.templates.usedN": "{n} kez kullanıldı",
  "brands.templates.nameTaken": "Bu isim bu markada zaten kullanılıyor.",
  "brands.templates.editor.newTitle": "Yeni şablon",
  "brands.templates.editor.editTitle": "Şablonu düzenle",
  "brands.templates.field.name": "Şablon adı",
  "brands.templates.field.nameHint": "Sizin için anlamlı bir etiket (\"Pazartesi motivasyonu\", \"Haftalık ürün spotu\").",
  "brands.templates.field.platform": "Platform",
  "brands.templates.field.contentType": "İçerik tipi",
  "brands.templates.field.audienceKind": "Hedef kitle",
  "brands.templates.field.audienceKindHint": "Genel için boş bırakın veya hedef kitle etiketi (\"B2B-karar-vericiler\").",
  "brands.templates.field.language": "Dil",
  "brands.templates.field.tone": "Ton (isteğe bağlı)",
  "brands.templates.field.toneHint": "Boş ise markanın varsayılan sesini kullanır.",
  "brands.templates.field.body": "Şablon gövdesi",
  "brands.templates.field.bodyPlaceholder": "Bu hafta {{product_name}} ile tanışın — {{benefit}}\n\n🔗 {{url}}",
  "brands.templates.field.bodyHint": "Yer tutucular için {{degisken_adi}} kullanın. Aşağıdaki listede tanımlayın.",
  "brands.templates.field.vars": "Değişkenler",
  "brands.templates.field.varsEmpty": "Bu şablon değişken kullanmıyor.",
  "brands.templates.field.varName": "Ad",
  "brands.templates.field.varKind": "Tip",
  "brands.templates.field.varExample": "Örnek",
  "brands.templates.field.addVar": "Değişken ekle",
  "brands.templates.field.removeVar": "Kaldır",
  "brands.templates.field.isEnabled": "Etkin (AI bu şablonu seçebilir)",

  "brands.voice.title": "Marka sesi",
  "brands.voice.subtitle": "AI üretimi bu kaydırıcılara göre ayarlanır.",
  "brands.voice.customTitle": "Özel nitelikler",
  "brands.voice.customSubtitle": "Yukarıdaki sözlüğe girmeyen kendi eksenlerinizi ekleyin.",
  "brands.voice.addLabel": "Yeni nitelik",

  "brands.audience.demoTitle": "Demografi",
  "brands.audience.demoSubtitle": "Yapısal hedefleme — yaş, bölge, dil.",
  "brands.audience.psyTitle": "Psikografi",
  "brands.audience.psySubtitle": "İlgiler, değerler, dert noktaları, satın alma tetikleyicileri.",
  "brands.audience.ageRange": "Yaş aralığı",
  "brands.audience.genderSkew": "Cinsiyet eğilimi",
  "brands.audience.income": "Gelir düzeyi",
  "brands.audience.unspecified": "Belirtilmemiş",
  "brands.audience.regions": "Bölgeler / şehirler",
  "brands.audience.regionsPlaceholder": "Şehir veya bölge ara (ör. İstanbul)",
  "brands.audience.regionsHint": "Önerilerden seçin — kanonik kayıt için gerekli (Istanbul ≠ ist ≠ Istanbul/TR).",
  "brands.audience.languages": "Diller",
  "brands.audience.languagesPlaceholder": "Dil ara (ör. Türkçe)",
  "brands.audience.interests": "İlgi alanları",
  "brands.audience.interestsPlaceholder": "İlgi alanı ekle (ör. kahve)",
  "brands.audience.values": "Değerler",
  "brands.audience.valuesPlaceholder": "Değer ekle (ör. kalite)",
  "brands.audience.painPoints": "Dert noktaları",
  "brands.audience.painPointsPlaceholder": "Dert noktası ekle",
  "brands.audience.triggers": "Satın alma tetikleyicileri",
  "brands.audience.triggersPlaceholder": "Tetikleyici ekle",
  "brands.audience.freeAddHint": "Listede yoksa kendi cevabınızı yazıp Enter'a basın.",

  "brands.products.title": "Ürünler",
  "brands.products.subtitle": "Sattığınız somut ürünler — AI üretiminde bağlam olarak kullanılır.",
  "brands.products.add": "Ürün ekle",
  "brands.products.newTitle": "Yeni ürün",
  "brands.products.empty": "Henüz ürün eklenmedi.",
  "brands.products.name": "Ad",
  "brands.products.category": "Kategori",
  "brands.products.categoryNone": "Kategori yok",
  "brands.products.description": "Açıklama",
  "brands.products.features": "Özellikler",
  "brands.products.featuresPlaceholder": "Özellik ekle",
  "brands.products.featuresHint": "Her özelliği Enter ile ekleyin.",

  "brands.services.title": "Hizmetler",
  "brands.services.subtitle": "Sunduğunuz hizmetler — danışmanlık, paketler, abonelikler.",
  "brands.services.add": "Hizmet ekle",
  "brands.services.newTitle": "Yeni hizmet",
  "brands.services.empty": "Henüz hizmet eklenmedi.",
  "brands.services.name": "Ad",
  "brands.services.targetAudience": "Hedef kitle",
  "brands.services.priceHint": "Fiyat ipucu",
  "brands.services.priceHintPlaceholder": "ör. ₺2.500'den başlayan",
  "brands.services.description": "Açıklama",

  "brands.topics.title": "Konular",
  "brands.topics.subtitle": "Konuşulacak ve kaçınılacak konular — AI üretimi bu listelere uyar.",
  "brands.topics.includeTitle": "Konuşulacaklar",
  "brands.topics.avoidTitle": "Kaçınılacaklar",
  "brands.topics.empty": "Henüz konu eklenmedi.",
  "brands.topics.addInclude": "Konuşulacak konu ekle",
  "brands.topics.addAvoid": "Kaçınılacak konu ekle",
  "brands.topics.includeHint": "ör. yeni ürün lansmanı, kampanyalar",
  "brands.topics.avoidHint": "ör. rakip karşılaştırmaları",

  "brands.examples.title": "Örnek paylaşımlar ve varlıklar",
  "brands.examples.subtitle": "Markanıza ait sevdiğiniz örnek paylaşımları, logoları ve referans görselleri buraya yükleyin. Yapay zeka içerik üretirken bu örnekleri referans alır.",
  "brands.examples.uploadAs": "Yükleme türü",
  "brands.examples.dropHere": "Dosyaları buraya sürükleyin veya tıklayın",
  "brands.examples.dropHint": "JPEG, PNG, WebP, GIF veya PDF · en fazla 15 MB",
  "brands.examples.dropZoneLabel": "Dosya yükleme alanı",
  "brands.examples.empty": "Henüz dosya yüklenmedi.",
  "brands.examples.tooBig": "Dosya 15 MB sınırını aşıyor.",
  "brands.examples.queued": "Sırada",
  "brands.examples.uploading": "Yükleniyor",
  "brands.examples.done": "Yüklendi",
  "brands.examples.kind.example": "Örnek paylaşım",
  "brands.examples.kind.logo": "Logo",
  "brands.examples.kind.product": "Ürün",
  "brands.examples.kind.team": "Ekip",
  "brands.examples.kind.background": "Arka plan",
  "brands.examples.kind.document": "Belge",
  "brands.examples.kind.other": "Diğer",
  "brands.examples.filter.all": "Tümü",
  "brands.examples.filter.example": "Örnek paylaşımlar",
  "brands.examples.filter.logo": "Logolar",
  "brands.examples.filter.product": "Ürünler",
  "brands.examples.filter.background": "Arka planlar",
  "brands.examples.filter.document": "Belgeler",
  "brands.examples.filter.other": "Diğer",

  "common.delete": "Sil",
  "common.confirm": "Onayla",

  "taginput.addFree": "{value} ekle",
  "taginput.noResults": "Sonuç bulunamadı.",

  "admin.health.nav": "Sistem",
  "admin.health.navTitle": "Sistem sağlığı paneli",
  "admin.health.eyebrow": "SİSTEM · STATUS BOARD",
  "admin.health.title": "Sistem sağlığı",
  "admin.health.subtitle": "Her 30 saniyede bir tüm uç noktaları senkronize doğrular.",
  "admin.health.lastFetched": "Son güncelleme",
  "admin.health.refresh": "Yenile",
  "admin.health.total": "Toplam",
  "admin.health.passing": "Geçen",
  "admin.health.failing": "Başarısız",
  "admin.health.criticalFailing": "Kritik başarısız",
  "admin.health.section.critical": "Kritik",
  "admin.health.section.warning": "Uyarı",
  "admin.health.section.info": "Bilgi",
  "admin.health.overall.ok": "Her şey yolunda",
  "admin.health.overall.down": "Kritik arıza",
  "admin.health.overall.unknown": "Bilinmiyor",
  "admin.health.severity.critical": "Kritik",
  "admin.health.severity.warning": "Uyarı",
  "admin.health.severity.info": "Bilgi",
  "admin.health.checked": "Kontrol edildi",

  "brands.kit.logoTitle": "Logo",
  "brands.kit.logoSubtitle": "İçerik üretiminde referans olarak kullanılan birincil marka logosu.",
  "brands.kit.noLogo": "Logo yok",
  "brands.kit.uploadLogo": "Logo yükle",
  "kit.progress.uploadingTitle": "Marka kiti yükleniyor",
  "kit.progress.reading": "Belgeniz okunuyor",
  "kit.progress.colors": "Marka renkleri hazırlanıyor",
  "kit.progress.voice": "Marka sesi süzülüyor",
  "kit.progress.fonts": "Yazı tipleri seçiliyor",
  "kit.progress.almost": "Son rötuşlar yapılıyor",
  "kit.progress.processingSub": "Lütfen sayfayı kapatmayın — birkaç saniye sürer.",
  "kit.progress.doneTitle": "Marka kiti hazır",
  "kit.progress.doneSubtitle": "Aşağıdaki önizlemeyi inceleyip uygulayabilirsiniz.",
  "kit.progress.errorTitle": "Marka kiti işlenemedi",
  "kit.progress.err.tooBig": "Dosya çok büyük. En fazla 8 MB olmalı. PDF'i sıkıştırıp tekrar deneyin ya da daha küçük bir versiyon yükleyin.",
  "kit.progress.err.noFile": "Dosya seçilmedi. Lütfen bir marka kiti yükleyin.",
  "kit.progress.err.generic": "Dosya işlenirken bir sorun oluştu. Lütfen tekrar deneyin.",
  "kit.jsRendered.title": "Marka kiti JavaScript ile oluşturulmuş",
  "kit.jsRendered.body": "Sadece renkleri okuyabildik. Logolar, yazılar ve gönderi şablonları sayfa tarayıcıda açıldıktan SONRA JavaScript ile oluşturuluyor — sunucumuz JS çalıştırmıyor. Çözüm:",
  "kit.jsRendered.step1": "Marka kiti dosyasını tarayıcınızda açın (Chrome/Safari fark etmez).",
  "kit.jsRendered.step2": "Sayfa tamamen yüklendikten sonra Dosya → Farklı Kaydet → \"Web sayfası, eksiksiz\" seçin.",
  "kit.jsRendered.step3": "Kaydedilen .html dosyasını buraya tekrar yükleyin. Ya da logoyu ve örnek gönderileri aşağıda manuel ekleyin.",
  "brands.kit.replaceLogo": "Logoyu değiştir",
  "brands.kit.removeLogo": "Kaldır",
  "brands.kit.logoHint": "PNG, JPEG, WebP veya SVG · şeffaf arka plan tercih edilir",
  "brands.kit.paletteTitle": "Renk paleti",
  "brands.kit.paletteSubtitle": "Marka renkleri. AI görsel üretiminde referans olarak kullanılır.",
  "brands.kit.paletteEmpty": "Henüz renk eklenmedi.",
  "brands.kit.addColor": "Renk ekle",
  "brands.kit.swatch": "Renk",
  "brands.kit.hex": "Hex",
  "brands.kit.colorName": "İsim (opsiyonel)",
  "brands.kit.colorNamePlaceholder": "ör. Nexus Kırmızı",
  "brands.kit.role": "Rol",
  "brands.kit.typographyTitle": "Tipografi",
  "brands.kit.typographySubtitle": "Görsel + alıntı üretiminde kullanılacak yazı tipleri.",
  "brands.kit.displayFont": "Başlık yazı tipi",
  "brands.kit.bodyFont": "Gövde yazı tipi",
  "brands.kit.voiceSummaryTitle": "Ses özeti",
  "brands.kit.voiceSummarySubtitle": "Marka sesinin kısa, AI'ya iletilebilir özeti.",
  "brands.kit.voiceSummary": "Özet",
  "brands.kit.voiceSummaryHint": "Bir paragrafı geçmesin. Tüm AI çağrılarının sistem mesajına eklenir.",
  "brands.kit.source": "Kaynak",
  "brands.kit.sourceHint": "Bu kit kullanıcı tarafından mı, AI tarafından mı, yoksa karışık mı oluşturuldu?",
  "brands.kit.error.invalidHex": "Geçersiz hex kodu (#RRGGBB)",
  "brands.kit.error.fixPaletteFirst": "Önce paletteki hata(lar)ı düzeltin.",

  "content.title": "İçerik",
  "content.eyebrow": "İÇERİK STÜDYOSU · CONTENT STUDIO",
  "content.empty.title": "Henüz içerik üretilmedi",
  "content.empty.subtitle": "Bir markaya yönelik ilk gönderinizi üretmek için yapay zekâya bir komut verin.",
  "content.new": "Yeni içerik üret",
  "content.bulk.cta": "Toplu üret",
  "content.bulk.title": "Toplu içerik üretimi",
  "content.bulk.subtitle": "Bir tarih aralığında platform başına otomatik gönderi taslakları planlayın. Yapay zekâ her taslağı arka planda otomatik olarak çalıştırır.",
  "content.bulk.field.brand": "Marka",
  "content.bulk.field.prompt": "Konu / bu gönderiler ne hakkında olmalı?",
  "content.bulk.field.promptPlaceholder": "örn. yaz kampanyası, yeni ürün serisi, kulis ekip içerikleri, müşteri başarı hikâyeleri…",
  "content.bulk.field.promptHint": "İsteğe bağlı ama önerilir. Boş bırakırsanız her gönderi sadece tarih ve platforma göre üretilir ve hepsi birbirine çok benzer çıkar.",
  "content.bulk.field.startDate": "Başlangıç tarihi",
  "content.bulk.field.endDate": "Bitiş tarihi",
  "content.bulk.field.rangeHint": "En fazla 90 gün.",
  "content.bulk.field.counts": "Platform başına sayı",
  "content.bulk.field.countsHint": "Her platform için kaç gönderi planlamak istiyorsunuz? Aralığa eşit dağıtılır.",
  "content.bulk.field.totalPrefix": "Toplam:",
  "content.bulk.field.includeHolidays": "Pazar günlerini planla (kapatılırsa otomatik Pazartesi'ye kaydırılır)",
  "content.bulk.submit": "Taslakları oluştur",
  "content.bulk.err.brand": "Marka seçilmedi.",
  "content.bulk.err.platforms": "En az bir platform için bir adet girin.",
  "content.bulk.err.dateRange": "Bitiş tarihi başlangıç tarihinden önce olamaz.",
  "content.bulk.detail.title": "Toplu üretim",
  "content.bulk.detail.progress": "{done}/{total} hazır",
  "content.bulk.detail.failed": "başarısız",
  "content.bulk.detail.children": "Taslaklar",
  "content.bulk.detail.noChildren": "Henüz taslak yok.",
  "content.bulk.detail.unscheduled": "Planlanmamış",
  "content.bulk.detail.topic": "Konu",
  "content.bulk.detail.approveAll": "{n} taslağın hepsini onayla & planla",
  "content.bulk.detail.approveAllOverride": "Hepsini tek bir saatte planla…",
  "content.bulk.detail.approveAllOverrideHint": "Spread tarihlerini kullanmak yerine tüm onaylananları seçtiğiniz tek bir saatte yayınla",
  "content.bulk.detail.acceptAndSchedule": "Onayla & planla",
  "content.bulk.detail.viewDetail": "Tüm seçenekleri gör →",
  "content.pickBrand": "Önce bir marka seçin",
  "content.pickBrand.hint": "Her marka kendi içerik takvimini, sesini ve sosyal hesaplarını taşır.",
  "content.openCalendar": "Takvimi aç",
  "content.recentGens": "Son üretimler",
  "content.recentPosts": "Son gönderiler",
  "content.brandTab": "İçerik",
  "content.gen.status.pending": "Bekliyor",
  "content.gen.status.running": "Çalışıyor",
  "content.gen.status.ready": "Hazır",
  "content.gen.status.failed": "Başarısız",
  "content.gen.status.approved": "Onaylandı",
  "content.gen.status.rejected": "Reddedildi",
  "content.gen.status.abandoned": "Terk edildi",
  "content.post.status.draft": "Taslak",
  "content.post.status.scheduled": "Planlandı",
  "content.post.status.publishing": "Yayınlanıyor",
  "content.post.status.published": "Yayınlandı",
  "content.post.status.failed": "Başarısız",
  "content.post.status.cancelled": "İptal edildi",

  "gen.create.title": "Yeni içerik üret",
  "gen.create.subtitle": "Yapay zekâ için kısa bir brief yazın; markanızın sesine uygun, platforma özel içerik üretelim.",
  "gen.field.brand": "Marka",
  "gen.field.prompt": "Komut",
  "gen.field.promptHint": "Ne anlatmak istediğinizi bir paragrafla açıklayın. Örn. 'Yeni Galata şubemizin açılışını duyuralım, samimi ve sıcak bir ton kullan.'",
  "gen.field.postType": "Gönderi türü",
  "gen.field.postType.feed": "Feed gönderisi",
  "gen.field.postType.story": "Hikaye",
  "gen.field.postType.storyHint": "Hikayeler 9:16 dikey formattadır. Yalnızca Instagram ve Facebook desteklenir; başlık 3-8 kelime, açıklama yok, en fazla 1 hashtag. Hikayeler 24 saat sonra otomatik kaybolur.",
  "gen.field.platforms": "Hedef platformlar",
  "gen.field.platforms.required": "En az bir platform seçin.",
  "gen.field.platforms.noStoryApi": "Bu platformun hikaye (story) API'si yok",
  "gen.field.platforms.notConnected": "Bu platform bu marka için henüz bağlanmamış",
  "gen.field.platforms.connectHint": "Görmediğin bir platform mu var?",
  "gen.field.platforms.connectCTA": "Önce bu platformu Entegrasyonlar sayfasından bağla",
  "schedule.title": "{n} gönderiyi zamanla",
  "schedule.subtitle": "Onaylanan gönderiler ne zaman yayınlansın? Varsayılan yarım saat sonrasıdır; değiştirebilirsiniz.",
  "schedule.openCta": "{n} gönderiyi zamanla →",
  "schedule.confirm": "{n} gönderiyi zamanla",
  "schedule.preset.in1h": "⚡ 1 saat içinde",
  "schedule.preset.tmrw10": "📅 Yarın 10:00",
  "schedule.preset.fri10": "📅 Cuma 10:00",
  "schedule.preset.custom": "🗓️ Tarih seç…",
  "schedule.saveAsDraft": "Taslak olarak kaydet, sonra karar veririm",
  "gen.field.contentTypes": "İçerik türleri",
  "gen.create.submit": "Üret",
  "gen.create.generating": "Üretiliyor...",

  "gen.detail.title": "Üretim sonucu",
  "gen.detail.prompt": "Komut",
  "gen.detail.versions": "Versiyonlar",
  "gen.detail.approve": "Seçili versiyonları onayla",
  "gen.detail.approveAll": "Hepsini onayla ve taslakları oluştur",
  "gen.detail.approveSubtitle": "Her platformda kabul ettiğiniz versiyondan bir taslak gönderi oluşturulur.",
  "gen.detail.needAccepted": "Devam etmek için en az bir versiyonu kabul edin.",
  "gen.detail.partialApproveHint": "Kabul edilen platformlar için taslak oluşturulacak. Atlanan platformlar: {platforms}.",
  "gen.detail.approveAccepted": "Kabul edilen {n} platform için taslakları oluştur",
  "gen.detail.totalCost": "Toplam maliyet",
  "gen.detail.accept": "Kabul et",
  "gen.detail.reject": "Reddet",
  "gen.detail.statusPending": "Beklemede",
  "gen.detail.statusAccepted": "Kabul edildi",
  "gen.detail.statusRejected": "Reddedildi",
  "gen.detail.variantPickHint": "Bir varyant seçin",
  "gen.detail.variantAcceptedHere": "Seçildi",
  "gen.detail.noText": "Metin yok.",
  "gen.detail.editText": "Metni düzenle",
  "gen.detail.replaceImage": "Görseli değiştir",
  "gen.detail.save": "Kaydet",
  "gen.detail.cancel": "Vazgeç",
  "gen.detail.hashtagsPlaceholder": "kahve, istanbul, sabah",
  "gen.detail.hashtagsHint": "Virgülle ayırın; #işareti otomatik eklenir.",
  "gen.detail.image.noAssets": "Bu markaya ait görsel yok. Marka Stüdyosu → Örnekler'den yükleyin.",
  "gen.detail.image.clear": "Görseli kaldır",
  "gen.detail.regenerate.cta": "Yeni bir tasarım dene",
  "gen.detail.regenerate.title": "{platform} için yeni deneme",
  "gen.detail.regenerate.placeholder": "İsteğe bağlı not: \"daha samimi olsun\", \"kurumsal değil\"…",
  "gen.detail.regenerate.submit": "Yeniden üret",

  // Calendar view (v0.3.9)
  "calendar.cta": "Takvim",
  "calendar.title": "İçerik takvimi",
  "calendar.prev": "Önceki",
  "calendar.next": "Sonraki",
  "calendar.legend.published": "Yayında",
  "calendar.legend.scheduled": "Planlandı",
  "calendar.legend.draft": "Taslak",
  "calendar.legend.stub": "Bekleyen taslak",
  "calendar.legend.failed": "Başarısız",
  "calendar.empty.title": "Bu marka için henüz planlanmış içerik yok.",
  "calendar.empty.hint": "Yeni içerik üret veya toplu üretim kullanarak takvimi doldur.",

  // Comments inbox (v0.4.0)
  "inbox.eyebrow": "YORUMLAR · COMMENTS",
  "inbox.title": "Yorum gelen kutusu",
  "inbox.pickBrand": "Yorumlarını incelemek istediğiniz markayı seçin.",
  "inbox.empty": "Bu filtre için yorum yok.",
  "inbox.pickHint": "Sol panelden bir yorum seçin.",
  "inbox.category": "Kategori",
  "inbox.hide": "Gizle",
  "inbox.escalate": "Yükselt",
  "inbox.tab.all": "Tümü",
  "inbox.tab.new": "Yeni",
  "inbox.tab.draftPending": "Taslak hazır",
  "inbox.tab.escalated": "Yükseltildi",
  "inbox.status.new": "Yeni",
  "inbox.status.classifying": "Sınıflandırılıyor",
  "inbox.status.draft_pending": "Taslak hazır",
  "inbox.status.approved": "Onaylandı",
  "inbox.status.sent": "Gönderildi",
  "inbox.status.hidden": "Gizlendi",
  "inbox.status.escalated": "Yükseltildi",
  "inbox.drafts.title": "AI yanıt taslakları",
  "inbox.drafts.empty": "Henüz taslak yok. \"Yanıt üret\" ile başlayın.",
  "inbox.drafts.generate": "Yanıt üret",
  "inbox.drafts.edit": "Düzenle",
  "inbox.drafts.approve": "Onayla",
  "inbox.drafts.cancel": "Vazgeç",
  "inbox.drafts.save": "Kaydet",
  "inbox.drafts.sent": "gönderildi",
  "inbox.drafts.approved": "onaylandı",
  "gen.detail.cost": "Maliyet",
  "gen.detail.latency": "Süre",
  "gen.detail.model": "Model",
  "gen.detail.notReady": "Onay için üretimin 'hazır' durumunda olması gerekir.",
  "gen.detail.approvedNote": "Bu üretim onaylandı. Aşağıdaki versiyonlardan taslak gönderiler oluşturuldu.",
  "gen.field.variants": "Platform başına varyant",
  "gen.field.variantsHint": "Her platform için kaç farklı taslak üretilsin. Daha fazla varyant = daha fazla maliyet.",
  "gen.field.references": "Referans örnekler",
  "gen.field.referencesHint": "En fazla 5 örnek seçin. Yapay zeka bunları görsel + üslup referansı olarak kullanır.",
  "gen.field.referencesEmpty": "Bu marka için henüz örnek paylaşım yüklenmedi.",
  "gen.field.referencesEmptyCTA": "Marka Stüdyosu'na git →",

  "post.detail.title": "Gönderi detayı",
  "post.detail.backToGallery": "Galeriye dön",
  "post.detail.body": "Metin",
  "post.detail.hashtags": "Hashtagler",
  "post.detail.image": "Görsel",
  "post.detail.edit": "Düzenle",
  "post.detail.cancel": "İptal",
  "post.detail.save": "Kaydet",
  "post.detail.schedule": "Planla",
  "post.detail.scheduleAt": "Yayın zamanı",
  "post.detail.scheduleHint": "En az 1 dakika sonrası olmalı.",
  "post.detail.scheduleSubmit": "Planla",
  "post.detail.cancelPost": "Gönderiyi iptal et",
  "post.detail.deletePost": "Gönderiyi sil",
  "post.detail.deleteConfirm.title": "Bu gönderiyi silmek istediğinizden emin misiniz?",
  "post.detail.deleteConfirm.body": "Silinen gönderiler geri alınamaz.",
  "post.detail.scheduledFor": "Planlandı",
  "post.detail.publishedAt": "Yayınlandı",
  "post.detail.notEditable": "Bu gönderi düzenlenemez (yalnızca taslak/planlanmış gönderiler).",
  "post.detail.aiDisclosure": "AI etiketi göster",
  "post.detail.publishTelegram": "Telegram'da yayınla",
  "post.detail.publishTelegramSubtitle": "Bu gönderiyi şu anda markanın Telegram kanalında paylaşılan bot (@nexus_dijital_bot) aracılığıyla yayınla.",
  "post.detail.publishNow": "Şimdi yayınla",
  "post.detail.publishedTitle": "Telegram'da yayınlandı",
  "post.detail.failedTitle": "Yayın başarısız",
  "post.detail.status": "Durum",
  "post.detail.dangerZone": "Tehlikeli alan",
  "post.detail.reschedule": "Yeniden planla",
  "post.detail.cancelConfirm.title": "Bu gönderiyi iptal et?",
  "post.detail.cancelConfirm.body": "Gönderi taslağa döner ve yayınlanmaz. Daha sonra yeniden planlayabilirsiniz.",
  "brands.kit.importTitle": "Marka kitini içe aktar",
  "brands.kit.importSubtitle": "Bir HTML marka kiti yükleyin; renkler, fontlar, ses tonu, logo ve şablonları otomatik çıkarırız. Hangilerini uygulayacağınızı bir sonraki ekranda seçeceksiniz.",
  "brands.kit.importButton": "HTML dosyası seç",
  "brands.kit.importReviewTitle": "İçe aktarılacaklar",
  "brands.kit.importReviewSubtitle": "Hangi öğelerin uygulanacağını seçin. Hiçbir şey otomatik kaydedilmez — uyguladıktan sonra mevcut paneli inceleyip Kaydet'e basın.",
  "brands.kit.importApply": "Seçilenleri uygula",
  "brands.kit.importSectionPalette": "Renk paleti",
  "brands.kit.importSectionTypography": "Tipografi",
  "brands.kit.importSectionVoice": "Marka sesi",
  "brands.kit.importSectionLogo": "Logo",
  "brands.kit.importSectionTemplates": "Gönderi şablonları",
  "brands.kit.importNone": "Bu bölümde içe aktarılacak öğe bulunamadı.",
  "brands.aiAssist.title": "AI ile marka kurulumu",
  "brands.aiAssist.badge": "Yapay zeka",
  "brands.aiAssist.subtitle": "İşletmenizi bir paragrafla anlatın — sektör, marka sesi, hedef kitle, ürünler, hizmetler ve içerik konuları için taslakları AI üretsin. Hepsi gözden geçirildikten sonra uygulanır.",
  "brands.aiAssist.openButton": "AI'a yardım ettir",
  "brands.aiAssist.descTitle": "İşletmenizi tanıtın",
  "brands.aiAssist.descSubtitle": "Ne yaptığınızı, kim için olduğunu ve sizi farklı kılan şeyi yazın. Ne kadar net olursanız, taslak o kadar isabetli olur.",
  "brands.aiAssist.descLabel": "Açıklama",
  "brands.aiAssist.descPlaceholder": "Örnek: 2019'dan beri Kadıköy'de el yapımı seramik atölyesi işletiyorum. Her parça benzersiz, çevre dostu boyalarla. Müşterilerim 25-40 yaş, tasarım meraklısı kadınlar...",
  "brands.aiAssist.descHint": "En az 1-2 cümle. AI buradan sektör, ses tonu, hedef kitle ve içerik fikirleri üretir.",
  "brands.aiAssist.generate": "Taslak üret",
  "brands.aiAssist.errorTooShort": "Lütfen işletmenizi en az bir cümlede tanıtın (en az 20 karakter).",
  "brands.aiAssist.reviewTitle": "Önerilen taslak",
  "brands.aiAssist.reviewSubtitle": "Hangi bölümlerin uygulanacağını seçin. İşaretledikleriniz mevcut tüm bölümlerinize eklenir/yazılır.",
  "brands.aiAssist.apply": "Seçilenleri uygula",
  "brands.aiAssist.sectionGeneral": "Sektör ve kategori",
  "brands.aiAssist.sectionVoice": "Marka sesi",
  "brands.aiAssist.sectionAudience": "Hedef kitle",
  "brands.aiAssist.sectionProducts": "Ürünler",
  "brands.aiAssist.sectionServices": "Hizmetler",
  "brands.aiAssist.sectionTopics": "İçerik konuları",
  "brands.aiAssist.setupTitle": "AI ile markanızı kurun",
  "brands.aiAssist.setupSubtitle": "İstediklerinizi doldurun. Her şey isteğe bağlı — sadece açıklama bile yeter. AI hepsini birlikte işler ve tek bir gözden geçirme ekranı sunar.",
  "brands.aiAssist.errorEmpty": "Lütfen en az bir alanı doldurun (açıklama, marka kiti, logo veya örnek gönderi).",
  "brands.aiAssist.statusWorking": "İşleniyor...",
  "brands.aiAssist.kitLabel": "Marka kitini yükle (HTML)",
  "brands.aiAssist.kitHint": "Bir HTML marka kitiniz varsa, renkler ve fontlar otomatik olarak çıkarılır.",
  "brands.aiAssist.kitChoose": "HTML dosyası seç",
  "brands.aiAssist.kitReplace": "Değiştir",
  "brands.aiAssist.logoLabel": "Logoyu yükle",
  "brands.aiAssist.logoHint": "PNG, JPG veya WebP. Logo doğrudan marka varlıklarına yüklenir.",
  "brands.aiAssist.logoChoose": "Logo seç",
  "brands.aiAssist.logoReplace": "Değiştir",
  "brands.aiAssist.samplesLabel": "Örnek gönderiler",
  "brands.aiAssist.samplesHint": "AI'nın markanızın görsel stilini öğrenmesi için 1-10 örnek görsel ekleyin. Daha sonra üretimde referans olarak kullanılır.",
  "brands.aiAssist.samplesChoose": "Görselleri seç",
  "brands.aiAssist.samplesCount": "görsel seçildi",
  "brands.aiAssist.remove": "Kaldır",
  "brands.aiAssist.logoUploaded": "Logo yüklendi ✓",
  "brands.aiAssist.samplesUploaded": "Yüklenen örnek gönderiler",
  "brands.aiAssist.logoVariantsTitle": "Logo varyantları",
  "brands.aiAssist.logoVariantsHint": "Markanızın hangi versiyonunun ana logo olacağını seçin. Diğerleri AI görsel üretiminde referans olarak kullanılmak üzere kaydedilir (koyu/açık zemin, renkli zemin gibi).",
  "brands.aiAssist.logoPrimary": "Ana",
  "brands.aiAssist.logoMakePrimary": "Ana yap",
  "brands.aiAssist.logoInclude": "Ekle",
  "app.nav.primary": "Ana menü",
  "app.nav.openMenu": "Menüyü aç",
  "app.nav.thisBrand": "Bu marka",
  "app.nav.calendar": "Takvim",
  "app.nav.comments": "Yorumlar",
  "app.nav.gallery": "Galeri",
  "app.nav.sectionMain": "Ana",
  "app.nav.sectionAccount": "Hesap",
  "app.nav.search": "Ara",
  "app.nav.notifications": "Bildirimler",
  "app.nav.signedInAs": "Oturum açık",
  "gen.detail.platformsLabel": "platform",
  "gen.detail.variantSingular": "varyant",
  "gen.detail.variantPlural": "varyant",
  "gen.detail.progressLabel": "İlerleme",
  "gen.detail.readyToApprove": "Onaylamaya hazır ✓",
  "gen.detail.debugPrompt": "Modele giden gerçek prompt'u göster",
};

const en: Dict = {
  "brand.name": "Nexus Dijital",
  "common.continue": "Continue",
  "common.back": "Back",
  "common.signOut": "Sign out",
  "common.loading": "Loading…",
  "common.errorGeneric": "Something went wrong. Please try again.",

  "auth.login.title": "Welcome back",
  "auth.login.subtitle": "Sign in to your account.",
  "auth.login.email": "Email",
  "auth.login.password": "Password",
  "auth.login.submit": "Sign in",
  "auth.login.toSignup": "No account? Sign up.",
  "auth.login.forgot": "Forgot your password?",

  // /verify-email
  "verifyEmail.verifying": "Verifying your email…",
  "verifyEmail.verifyingHint": "This usually takes a couple of seconds.",
  "verifyEmail.verified": "Email verified",
  "verifyEmail.verifiedHint": "{email} is confirmed. You can sign in now.",
  "verifyEmail.continueToLogin": "Sign in",
  "verifyEmail.cannotVerify": "Couldn't verify",
  "verifyEmail.missingToken": "Verification link is missing or invalid. Please use the link in your email.",
  "verifyEmail.mismatch": "Your email address changed after this link was sent. Request a fresh verification.",
  "verifyEmail.expired": "Link expired or already used. Request a fresh verification.",
  "verifyEmail.backToLogin": "Back to login",

  // /forgot-password
  "forgotPassword.title": "Forgot your password?",
  "forgotPassword.subtitle": "Enter your email and we'll send a reset link.",
  "forgotPassword.email": "Email",
  "forgotPassword.sendLink": "Send reset link",
  "forgotPassword.sentTitle": "Link on its way",
  "forgotPassword.sentBody": "If this address is registered you'll receive an email shortly. Don't forget to check your spam folder.",
  "forgotPassword.backToLogin": "Back to login",

  // /reset-password
  "resetPassword.title": "Choose a new password",
  "resetPassword.subtitle": "Enter your new password twice. All active sessions will be signed out.",
  "resetPassword.newPassword": "New password",
  "resetPassword.confirm": "Confirm new password",
  "resetPassword.submit": "Update password",
  "resetPassword.tooShort": "Password must be at least 8 characters",
  "resetPassword.mismatch": "Passwords don't match",
  "resetPassword.expired": "Reset link expired or already used. Request a fresh one.",
  "resetPassword.missingToken": "Invalid link",
  "resetPassword.missingTokenBody": "This link is missing or malformed. Please request a fresh reset.",
  "resetPassword.requestNew": "Request a new link",
  "resetPassword.revokesAllSessions": "A successful reset signs you out of every device.",

  "auth.login.badCredentials": "Invalid email or password.",

  "auth.signup.title": "Create your account",
  "auth.signup.subtitle": "Get started in seconds.",
  "auth.signup.displayName": "Full name",
  "auth.signup.email": "Email",
  "auth.signup.password": "Password",
  "auth.signup.passwordHint": "At least 8 characters. Mixed case + numbers + symbols recommended.",
  "auth.signup.submit": "Create account",
  "auth.signup.toLogin": "Already have an account? Sign in.",
  "auth.signup.emailTaken": "That email is already registered.",
  "auth.signup.passwordShort": "Password must be at least 8 characters.",
  "auth.signup.confirmPassword": "Confirm password",
  "auth.signup.passwordMismatch": "Passwords don't match.",
  "auth.signup.strength.weak": "Weak",
  "auth.signup.strength.fair": "Fair",
  "auth.signup.strength.good": "Good",
  "auth.signup.strength.strong": "Strong",
  "auth.signup.displayNameRequired": "Full name is required.",

  "onboarding.title": "Create your first workspace",
  "onboarding.subtitle": "A workspace holds your brands, content, and team.",
  "onboarding.name": "Workspace name",
  "onboarding.slug": "URL slug",
  "onboarding.slugHint": "lowercase letters, numbers, hyphens. 2–32 chars.",
  "onboarding.submit": "Create workspace",
  "onboarding.slugTaken": "That slug is already taken.",

  "app.title": "Dashboard",
  "app.welcome": "Welcome, {name}.",
  "app.placeholder": "Brands, content and analytics are coming here soon.",
  "app.workspaceLabel": "Workspace",
  "app.home": "Home",
  "app.settings": "Settings",

  "notfound.eyebrow": "PAGE NOT FOUND · 404",
  "notfound.title": "We couldn't find that page.",
  "notfound.subtitle": "The link may be wrong, or the page may have moved.",
  "notfound.cta": "Back to home",

  "error.eyebrow": "AN ERROR OCCURRED · ERROR",
  "error.title": "Something went wrong.",
  "error.subtitle": "We hit a snag. Want to try again?",
  "error.refresh": "Try again",
  "error.toHome": "Back to home",

  "settings.title": "Account settings",
  "settings.profile": "Profile",
  "settings.email": "Email",
  "settings.emailNote": "Contact support to change your email address.",
  "settings.displayName": "Full name",
  "settings.locale": "Language",
  "settings.save": "Save",
  "settings.saved": "Saved.",
  "settings.workspace": "Workspace",
  "settings.workspaceSlug": "URL slug",
  "settings.workspaceName": "Workspace name",
  "settings.workspaceTimezone": "Timezone",
  "settings.workspaceLocale": "Workspace language",
  "settings.workspaceEditHint":
    "Only the owner or an admin can edit. The URL slug is permanent.",
  "settings.workspaceUpdated": "Workspace updated.",
  "settings.members": "Members",
  "settings.membersSubtitle":
    "People with access to this workspace. New members must already have an account.",
  "settings.membersEmpty": "No members yet.",
  "settings.addMember": "Add member",
  "settings.addMemberEmail": "User email",
  "settings.addMemberRole": "Role",
  "settings.addMemberCta": "Add",
  "settings.memberRole.owner": "Owner",
  "settings.memberRole.admin": "Admin",
  "settings.memberRole.editor": "Editor",
  "settings.memberRole.approver": "Approver",
  "settings.memberRole.viewer": "Viewer",
  "settings.memberSelf": "(you)",
  "settings.memberRemove": "Remove",
  "settings.memberLeave": "Leave workspace",
  "settings.memberRemoveConfirm": "Really remove this member?",
  "settings.memberLeaveConfirm":
    "Leaving this workspace means losing access to its data. Continue?",
  "settings.error.notFoundEmail": "No user is registered with that email.",
  "settings.error.alreadyMember": "That user is already a member.",
  "settings.error.forbidden": "You don't have permission for this action.",
  "settings.error.cannotRemoveOwner": "The workspace owner cannot be removed.",
  "settings.invite.sent": "Invitation sent to {email}.",
  "settings.invite.alreadyPending": "An invitation for {email} is already pending.",

  // /accept-invite
  "acceptInvite.accepting": "Accepting invitation…",
  "acceptInvite.acceptingHint": "This usually takes a couple of seconds.",
  "acceptInvite.accepted": "Invitation accepted",
  "acceptInvite.acceptedHint": "You've been added to {workspace}. Continuing…",
  "acceptInvite.continue": "Continue",
  "acceptInvite.expired": "Invitation link expired",
  "acceptInvite.expiredHint": "This link expired or was already used. Ask the inviter to send a new one.",
  "acceptInvite.missingToken": "Invalid link",
  "acceptInvite.missingTokenHint": "Invitation link is missing or malformed. Ask the inviter to resend.",
  "acceptInvite.backToLogin": "Back to login",
  "settings.signOut": "Sign out",

  "brands.title": "Brands",
  "brands.eyebrow": "BRAND STUDIO · MARKA STÜDYOSU",
  "brands.empty.title": "No brands yet",
  "brands.empty.subtitle": "Add your first brand to get started. Each brand carries its own content calendar, voice, and social accounts.",
  "brands.new": "New brand",
  "brands.openDetail": "Open detail",
  "brands.create.title": "New brand",
  "brands.create.subtitle": "Enter the basics. You can refine everything later.",
  "brands.field.legalName": "Legal name",
  "brands.field.legalNameHint": "Full legal entity name (e.g. Mesh Yazılım Teknoloji Limited Şirketi).",
  "brands.field.tradeName": "Trade name",
  "brands.field.tradeNameHint": "The name your audience knows you by.",
  "brands.field.slug": "URL slug",
  "brands.field.slugHint": "lowercase letters, numbers, hyphens. 2–32 chars, unique inside the workspace.",
  "brands.field.country": "Country",
  "brands.field.primaryLanguage": "Primary language",
  "brands.field.timezone": "Timezone",
  "brands.field.industry": "Industry",
  "brands.field.industryHint": "Optional. e.g. Software, Restaurant, Fashion.",
  "brands.field.category": "Category",
  "brands.field.categoryHint": "Optional. Narrower classification.",
  "brands.field.commentsMode": "Comments mode",
  "brands.field.commentsMode.approval": "Require approval",
  "brands.field.commentsMode.auto": "Publish automatically",
  "brands.field.telegramChannel": "Telegram channel",
  "brands.field.telegramChannelHint": "Where posts publish, e.g. @my_brand_news. Add @nexus_dijital_bot as an admin of the channel. The only supported publishing channel for now.",
  "brands.field.aiDisclosure": "Show AI-generated disclosure label",
  "brands.field.aiDisclosureHint": "Indicates that published content was prepared with AI assistance.",
  "brands.field.holidayPosts": "Suggest holiday-themed posts",
  "brands.create.submit": "Create brand",
  "brands.slugTaken": "That URL slug is already taken in this workspace.",
  "brands.detail.summary": "Overview",
  "brands.detail.edit": "Edit",
  "brands.detail.cancel": "Cancel",
  "brands.detail.save": "Save changes",
  "brands.detail.saved": "Saved.",
  "brands.detail.delete": "Delete brand",
  "brands.detail.deleteConfirm.title": "Delete this brand?",
  "brands.detail.deleteConfirm.body": "All associated brand kit, knowledge base, and assets will become inaccessible. This cannot be undone.",
  "brands.detail.deleteConfirm.confirm": "Yes, delete",
  "brands.detail.deleteConfirm.cancel": "Keep it",
  "brands.detail.kit.title": "Brand kit",
  "brands.detail.kit.empty": "No kit set yet.",
  "brands.detail.kb.title": "Knowledge base",
  "brands.detail.kb.empty": "No knowledge base content yet.",
  "brands.detail.kb.status": "Status",

  "brands.tabs.general": "General",
  "brands.tabs.voice": "Voice",
  "brands.tabs.audience": "Audience",
  "brands.tabs.products": "Products",
  "brands.tabs.services": "Services",
  "brands.tabs.topics": "Topics",
  "brands.tabs.examples": "Examples",
  "brands.tabs.templates": "Templates",
  "brands.tabs.integrations": "Integrations",

  // Integrations panel — EN
  "brands.integrations.title": "Social account connections",
  "brands.integrations.subtitle": "Connect the social accounts we'll publish to on behalf of this brand. While your Meta app is in Development mode only listed Testers can complete the flow; App Review unlocks publishing for everyone.",
  "brands.integrations.facebook": "Facebook Page",
  "brands.integrations.instagram": "Instagram Business",
  "brands.integrations.linkedin": "LinkedIn",
  "brands.integrations.meta.title": "Facebook + Instagram",
  "brands.integrations.meta.subtitle": "Meta OAuth connects your Pages + linked IG Business accounts. While in Dev mode only accounts listed as Testers in your app dashboard can connect.",
  "brands.integrations.linkedin.title": "LinkedIn",
  "brands.integrations.linkedin.subtitle": "Post to your personal LinkedIn profile. Company Page management awaiting MDP approval (4-12+ weeks).",
  "brands.integrations.x.title": "X (Twitter)",
  "brands.integrations.x.subtitle": "Connect your X account and manage your tweets from here. Text posts work now; image attach is coming soon.",
  "brands.integrations.x": "X",
  "brands.integrations.telegram.title": "Telegram Channel",
  "brands.integrations.telegram.subtitle": "Publish posts to a Telegram channel on behalf of your brand. All tenants share Nexus's platform bot — as the channel owner, you just add the bot as an administrator and paste your channel handle here.",
  "brands.integrations.telegram.step1.before": "Open ",
  "brands.integrations.telegram.step1.after": " in Telegram and start a chat with it (press the Start button).",
  "brands.integrations.telegram.step2": "Open your channel in Telegram → tap the channel header → Administrators → Add Administrator.",
  "brands.integrations.telegram.step3": "Search for the bot username shown above, select it, and leave \"Post Messages\" permission ON. No other permissions needed.",
  "brands.integrations.telegram.step4": "Paste your channel username (e.g. @nexusdijital_news) into the box below and press Save.",
  "brands.integrations.telegram.field": "Channel username or numeric chat ID",
  "brands.integrations.telegram.hint": "For public channels type @channelname; for private channels paste the numeric chat ID (e.g. -1001234567890).",
  "brands.integrations.telegram.save": "Save",
  "brands.integrations.telegram.saved": "Telegram channel saved.",
  "brands.integrations.telegram.connected": "Connected: {channel}",
  "brands.audience.regionScope": "Search scope",
  "brands.audience.regionScope.worldwide": "Worldwide",
  "brands.audience.regionScopeHint": "Limit region search to one country. Pick 'Worldwide' if you target multiple markets.",
  "brands.integrations.connect": "Connect",
  "brands.integrations.disconnect": "Disconnect",
  "brands.integrations.soon": "Soon",
  "brands.integrations.connected": "Accounts connected.",
  "brands.integrations.meta.pickerTitle": "Which pages should we connect to this brand?",
  "brands.integrations.meta.pickerSubtitle": "Every page you admin is listed below. Only pick the one(s) that belong to THIS brand — leave other brands' pages unchecked.",
  "brands.integrations.meta.pickerNoIG": "No Instagram Business account linked to this page",
  "brands.integrations.meta.pickerNoPages": "No pages available to connect.",
  "brands.integrations.meta.pickerPickAtLeastOne": "Select at least one page.",
  "brands.integrations.meta.pickerSelectedCount": "{n} page(s) selected",
  "brands.integrations.meta.pickerConfirm": "Connect selected",
  "brands.integrations.connected.title": "Connected accounts",
  "brands.integrations.connected.empty": "No accounts connected yet.",
  "brands.integrations.error.exchange_failed": "Meta token exchange failed — check the App ID/Secret + callback URL.",
  "brands.integrations.error.list_pages_failed": "Could not list your Pages. Try again.",
  "brands.integrations.error.no_pages": "No Facebook Pages found on this account.",
  "brands.tabs.kit": "Brand kit",
  "brands.tabs.knowledge": "Knowledge",

  // Templates panel — EN
  "brands.templates.title": "Post templates",
  "brands.templates.subtitle": "Reusable post skeletons. The AI picks a matching template when generating content, so recurring slots like \"Monday motivation\" or \"Weekly product spotlight\" feel like the same series each week.",
  "brands.templates.new": "New template",
  "brands.templates.empty": "No templates yet. Click \"New template\" to create the first one.",
  "brands.templates.edit": "Edit",
  "brands.templates.delete": "Delete",
  "brands.templates.paused": "Paused",
  "brands.templates.usedN": "Used {n} times",
  "brands.templates.nameTaken": "A template with this name already exists on this brand.",
  "brands.templates.editor.newTitle": "New template",
  "brands.templates.editor.editTitle": "Edit template",
  "brands.templates.field.name": "Template name",
  "brands.templates.field.nameHint": "A label that makes sense to you (\"Monday motivation\", \"Weekly product spotlight\").",
  "brands.templates.field.platform": "Platform",
  "brands.templates.field.contentType": "Content type",
  "brands.templates.field.audienceKind": "Audience kind",
  "brands.templates.field.audienceKindHint": "Leave \"general\" or use a tag (\"B2B-decision-makers\").",
  "brands.templates.field.language": "Language",
  "brands.templates.field.tone": "Tone (optional)",
  "brands.templates.field.toneHint": "Falls back to the brand's default voice when empty.",
  "brands.templates.field.body": "Template body",
  "brands.templates.field.bodyPlaceholder": "This week meet {{product_name}} — {{benefit}}\n\n🔗 {{url}}",
  "brands.templates.field.bodyHint": "Use {{variable_name}} for placeholders, then declare each one below.",
  "brands.templates.field.vars": "Variables",
  "brands.templates.field.varsEmpty": "This template uses no variables.",
  "brands.templates.field.varName": "Name",
  "brands.templates.field.varKind": "Kind",
  "brands.templates.field.varExample": "Example",
  "brands.templates.field.addVar": "Add variable",
  "brands.templates.field.removeVar": "Remove",
  "brands.templates.field.isEnabled": "Enabled (AI may pick this template)",

  "brands.voice.title": "Brand voice",
  "brands.voice.subtitle": "AI output is steered by these sliders. -1..+1.",
  "brands.voice.customTitle": "Custom attributes",
  "brands.voice.customSubtitle": "Add your own axes beyond the defaults above.",
  "brands.voice.addLabel": "New attribute",

  "brands.audience.demoTitle": "Demographics",
  "brands.audience.demoSubtitle": "Structural targeting — age, region, language.",
  "brands.audience.psyTitle": "Psychographics",
  "brands.audience.psySubtitle": "Interests, values, pain points, buying triggers.",
  "brands.audience.ageRange": "Age range",
  "brands.audience.genderSkew": "Gender skew",
  "brands.audience.income": "Income level",
  "brands.audience.unspecified": "Unspecified",
  "brands.audience.regions": "Regions / cities",
  "brands.audience.regionsPlaceholder": "Search a city or region (e.g. Istanbul)",
  "brands.audience.regionsHint": "Pick from suggestions — canonical storage matters (Istanbul ≠ ist ≠ Istanbul/TR).",
  "brands.audience.languages": "Languages",
  "brands.audience.languagesPlaceholder": "Search a language (e.g. Turkish)",
  "brands.audience.interests": "Interests",
  "brands.audience.interestsPlaceholder": "Add an interest (e.g. coffee)",
  "brands.audience.values": "Values",
  "brands.audience.valuesPlaceholder": "Add a value (e.g. quality)",
  "brands.audience.painPoints": "Pain points",
  "brands.audience.painPointsPlaceholder": "Add a pain point",
  "brands.audience.triggers": "Buying triggers",
  "brands.audience.triggersPlaceholder": "Add a trigger",
  "brands.audience.freeAddHint": "Type your own and press Enter if it's not in the list.",

  "brands.products.title": "Products",
  "brands.products.subtitle": "Concrete products you sell — used as context in AI output.",
  "brands.products.add": "Add product",
  "brands.products.newTitle": "New product",
  "brands.products.empty": "No products yet.",
  "brands.products.name": "Name",
  "brands.products.category": "Category",
  "brands.products.categoryNone": "No category",
  "brands.products.description": "Description",
  "brands.products.features": "Features",
  "brands.products.featuresPlaceholder": "Add a feature",
  "brands.products.featuresHint": "Press Enter to add each feature.",

  "brands.services.title": "Services",
  "brands.services.subtitle": "Services you offer — consulting, packages, subscriptions.",
  "brands.services.add": "Add service",
  "brands.services.newTitle": "New service",
  "brands.services.empty": "No services yet.",
  "brands.services.name": "Name",
  "brands.services.targetAudience": "Target audience",
  "brands.services.priceHint": "Price hint",
  "brands.services.priceHintPlaceholder": "e.g. from $50 / hr",
  "brands.services.description": "Description",

  "brands.topics.title": "Topics",
  "brands.topics.subtitle": "Topics to talk about and avoid — AI output respects both lists.",
  "brands.topics.includeTitle": "Talk about",
  "brands.topics.avoidTitle": "Avoid",
  "brands.topics.empty": "No topics yet.",
  "brands.topics.addInclude": "Add topic to talk about",
  "brands.topics.addAvoid": "Add topic to avoid",
  "brands.topics.includeHint": "e.g. new product launches, promotions",
  "brands.topics.avoidHint": "e.g. competitor comparisons",

  "brands.examples.title": "Example posts & assets",
  "brands.examples.subtitle": "Upload sample posts you love, logos, and reference images. The AI will use these as visual + tonal references when generating content.",
  "brands.examples.uploadAs": "Upload as",
  "brands.examples.dropHere": "Drop files here or click to pick",
  "brands.examples.dropHint": "JPEG, PNG, WebP, GIF or PDF · up to 15 MB",
  "brands.examples.dropZoneLabel": "File upload area",
  "brands.examples.empty": "No files uploaded yet.",
  "brands.examples.tooBig": "File exceeds 15 MB limit.",
  "brands.examples.queued": "Queued",
  "brands.examples.uploading": "Uploading",
  "brands.examples.done": "Uploaded",
  "brands.examples.kind.example": "Example post",
  "brands.examples.kind.logo": "Logo",
  "brands.examples.kind.product": "Product",
  "brands.examples.kind.team": "Team",
  "brands.examples.kind.background": "Background",
  "brands.examples.kind.document": "Document",
  "brands.examples.kind.other": "Other",
  "brands.examples.filter.all": "All",
  "brands.examples.filter.example": "Examples",
  "brands.examples.filter.logo": "Logos",
  "brands.examples.filter.product": "Products",
  "brands.examples.filter.background": "Backgrounds",
  "brands.examples.filter.document": "Documents",
  "brands.examples.filter.other": "Other",

  "common.delete": "Delete",
  "common.confirm": "Confirm",

  "taginput.addFree": "Add {value}",
  "taginput.noResults": "No matches.",

  "admin.health.nav": "System",
  "admin.health.navTitle": "System health board",
  "admin.health.eyebrow": "SYSTEM · STATUS BOARD",
  "admin.health.title": "System health",
  "admin.health.subtitle": "Every 30 seconds, every public endpoint gets synthetically exercised.",
  "admin.health.lastFetched": "Last fetched",
  "admin.health.refresh": "Refresh",
  "admin.health.total": "Total",
  "admin.health.passing": "Passing",
  "admin.health.failing": "Failing",
  "admin.health.criticalFailing": "Critical failing",
  "admin.health.section.critical": "Critical",
  "admin.health.section.warning": "Warning",
  "admin.health.section.info": "Info",
  "admin.health.overall.ok": "All systems go",
  "admin.health.overall.down": "Critical outage",
  "admin.health.overall.unknown": "Unknown",
  "admin.health.severity.critical": "Critical",
  "admin.health.severity.warning": "Warning",
  "admin.health.severity.info": "Info",
  "admin.health.checked": "Checked",

  "brands.kit.logoTitle": "Logo",
  "brands.kit.logoSubtitle": "Primary brand logo, used as visual reference in image generation.",
  "brands.kit.noLogo": "No logo",
  "brands.kit.uploadLogo": "Upload logo",
  "kit.progress.uploadingTitle": "Uploading brand kit",
  "kit.progress.reading": "Reading your document",
  "kit.progress.colors": "Lifting brand colors",
  "kit.progress.voice": "Distilling brand voice",
  "kit.progress.fonts": "Picking typography",
  "kit.progress.almost": "Almost ready",
  "kit.progress.processingSub": "Please don't close this page — a few seconds.",
  "kit.progress.doneTitle": "Brand kit ready",
  "kit.progress.doneSubtitle": "Review the preview below and apply.",
  "kit.progress.errorTitle": "Couldn't process the brand kit",
  "kit.progress.err.tooBig": "File too big. Max 8 MB — try compressing the PDF or splitting it.",
  "kit.progress.err.noFile": "No file selected. Please attach a brand kit.",
  "kit.progress.err.generic": "Something went wrong processing the file. Please try again.",
  "kit.jsRendered.title": "Brand kit is JavaScript-rendered",
  "kit.jsRendered.body": "We could only read the colors. Logos, voice text, and post templates are built AFTER the page opens in a browser via JavaScript — and our server doesn't execute JS. Fix:",
  "kit.jsRendered.step1": "Open the brand-kit HTML in your browser (Chrome / Safari either works).",
  "kit.jsRendered.step2": "After the page fully loads, choose File → Save As → \"Webpage, Complete\".",
  "kit.jsRendered.step3": "Re-upload the saved .html file here. Or add the logo and example posts manually below.",
  "brands.kit.replaceLogo": "Replace logo",
  "brands.kit.removeLogo": "Remove",
  "brands.kit.logoHint": "PNG, JPEG, WebP or SVG · transparent background preferred",
  "brands.kit.paletteTitle": "Color palette",
  "brands.kit.paletteSubtitle": "Brand colors. Used as reference in AI image generation.",
  "brands.kit.paletteEmpty": "No colors added yet.",
  "brands.kit.addColor": "Add color",
  "brands.kit.swatch": "Swatch",
  "brands.kit.hex": "Hex",
  "brands.kit.colorName": "Name (optional)",
  "brands.kit.colorNamePlaceholder": "e.g. Nexus Red",
  "brands.kit.role": "Role",
  "brands.kit.typographyTitle": "Typography",
  "brands.kit.typographySubtitle": "Fonts used in graphics + quote generation.",
  "brands.kit.displayFont": "Display font",
  "brands.kit.bodyFont": "Body font",
  "brands.kit.voiceSummaryTitle": "Voice summary",
  "brands.kit.voiceSummarySubtitle": "Short, AI-passable summary of the brand voice.",
  "brands.kit.voiceSummary": "Summary",
  "brands.kit.voiceSummaryHint": "One paragraph max. Attached to every AI call's system prompt.",
  "brands.kit.source": "Source",
  "brands.kit.sourceHint": "Was this kit built by the user, by AI, or both?",
  "brands.kit.error.invalidHex": "Invalid hex code (#RRGGBB)",
  "brands.kit.error.fixPaletteFirst": "Fix the palette error(s) first.",

  "content.title": "Content",
  "content.eyebrow": "CONTENT STUDIO · İÇERİK STÜDYOSU",
  "content.empty.title": "No content generated yet",
  "content.empty.subtitle": "Give the AI a brief to generate your first post for a brand.",
  "content.new": "Generate content",
  "content.bulk.cta": "Bulk generate",
  "content.bulk.title": "Bulk content generation",
  "content.bulk.subtitle": "Schedule a calendar of auto-drafted posts per platform across a date range. The AI runs every draft automatically in the background.",
  "content.bulk.field.brand": "Brand",
  "content.bulk.field.prompt": "Topic / what should these posts be about?",
  "content.bulk.field.promptPlaceholder": "e.g. summer sale launch, new product line, behind-the-scenes team series, customer success stories…",
  "content.bulk.field.promptHint": "Optional but strongly recommended. Without a topic every post is generated from date+platform alone, which collapses to generic on-brand fluff — all posts end up looking very similar.",
  "content.bulk.field.startDate": "Start date",
  "content.bulk.field.endDate": "End date",
  "content.bulk.field.rangeHint": "Up to 90 days.",
  "content.bulk.field.counts": "Posts per platform",
  "content.bulk.field.countsHint": "How many posts to schedule for each platform across the date range. Posts are spread evenly.",
  "content.bulk.field.totalPrefix": "Total:",
  "content.bulk.field.includeHolidays": "Schedule on Sundays (off = auto-bump to Monday)",
  "content.bulk.submit": "Create drafts",
  "content.bulk.err.brand": "No brand selected.",
  "content.bulk.err.platforms": "Pick at least one platform with a count.",
  "content.bulk.err.dateRange": "End date can't be before start date.",
  "content.bulk.detail.title": "Bulk generation",
  "content.bulk.detail.progress": "{done}/{total} ready",
  "content.bulk.detail.failed": "failed",
  "content.bulk.detail.children": "Drafts",
  "content.bulk.detail.noChildren": "No drafts yet.",
  "content.bulk.detail.unscheduled": "Unscheduled",
  "content.bulk.detail.topic": "Topic",
  "content.bulk.detail.approveAll": "Approve all {n} drafts & schedule",
  "content.bulk.detail.approveAllOverride": "Schedule all at one time…",
  "content.bulk.detail.approveAllOverrideHint": "Publish every approved post at a single time of your choice instead of the spread dates",
  "content.bulk.detail.acceptAndSchedule": "Accept & schedule",
  "content.bulk.detail.viewDetail": "See all options →",
  "content.pickBrand": "Pick a brand first",
  "content.pickBrand.hint": "Each brand carries its own content calendar, voice, and social accounts.",
  "content.openCalendar": "Open calendar",
  "content.recentGens": "Recent generations",
  "content.recentPosts": "Recent posts",
  "content.brandTab": "Content",
  "content.gen.status.pending": "Pending",
  "content.gen.status.running": "Running",
  "content.gen.status.ready": "Ready",
  "content.gen.status.failed": "Failed",
  "content.gen.status.approved": "Approved",
  "content.gen.status.rejected": "Rejected",
  "content.gen.status.abandoned": "Abandoned",
  "content.post.status.draft": "Draft",
  "content.post.status.scheduled": "Scheduled",
  "content.post.status.publishing": "Publishing",
  "content.post.status.published": "Published",
  "content.post.status.failed": "Failed",
  "content.post.status.cancelled": "Cancelled",

  "gen.create.title": "Generate content",
  "gen.create.subtitle": "Write a short brief; we'll produce platform-specific copy that matches your brand voice.",
  "gen.field.brand": "Brand",
  "gen.field.prompt": "Prompt",
  "gen.field.promptHint": "Describe what you want in a paragraph. e.g. 'Announce our new Galata location with a warm, friendly tone.'",
  "gen.field.postType": "Post type",
  "gen.field.postType.feed": "Feed post",
  "gen.field.postType.story": "Story",
  "gen.field.postType.storyHint": "Stories are 9:16 vertical. Only Instagram and Facebook are supported; headline 3-8 words, no body caption, max 1 hashtag. Stories disappear after 24 hours.",
  "gen.field.platforms": "Target platforms",
  "gen.field.platforms.required": "Pick at least one platform.",
  "gen.field.platforms.noStoryApi": "This platform has no story API",
  "gen.field.platforms.notConnected": "This platform isn't connected for this brand yet",
  "gen.field.platforms.connectHint": "Missing a platform?",
  "gen.field.platforms.connectCTA": "Connect it on the Integrations page first",
  "schedule.title": "Schedule {n} posts",
  "schedule.subtitle": "When should we publish the approved posts? Defaults to the next half-hour; change below.",
  "schedule.openCta": "Schedule {n} posts →",
  "schedule.confirm": "Schedule {n} posts",
  "schedule.preset.in1h": "⚡ In ~1 hour",
  "schedule.preset.tmrw10": "📅 Tomorrow 10:00",
  "schedule.preset.fri10": "📅 Friday 10:00",
  "schedule.preset.custom": "🗓️ Pick a date…",
  "schedule.saveAsDraft": "Save as draft, decide later",
  "gen.field.contentTypes": "Content types",
  "gen.create.submit": "Generate",
  "gen.create.generating": "Generating…",

  "gen.detail.title": "Generation result",
  "gen.detail.prompt": "Prompt",
  "gen.detail.versions": "Versions",
  "gen.detail.approve": "Approve selected versions",
  "gen.detail.approveAll": "Approve all & create drafts",
  "gen.detail.approveSubtitle": "One draft post per platform is created from the variant you accepted.",
  "gen.detail.needAccepted": "Accept at least one variant to continue.",
  "gen.detail.partialApproveHint": "Drafts will be created for accepted platforms. Skipped: {platforms}.",
  "gen.detail.approveAccepted": "Create drafts for {n} accepted platform(s)",
  "gen.detail.totalCost": "Total cost",
  "gen.detail.accept": "Accept",
  "gen.detail.reject": "Reject",
  "gen.detail.statusPending": "Pending",
  "gen.detail.statusAccepted": "Accepted",
  "gen.detail.statusRejected": "Rejected",
  "gen.detail.variantPickHint": "Pick a variant",
  "gen.detail.variantAcceptedHere": "Selected",
  "gen.detail.noText": "No text.",
  "gen.detail.editText": "Edit text",
  "gen.detail.replaceImage": "Replace image",
  "gen.detail.save": "Save",
  "gen.detail.cancel": "Cancel",
  "gen.detail.hashtagsPlaceholder": "coffee, istanbul, morning",
  "gen.detail.hashtagsHint": "Comma-separated; the # is added automatically.",
  "gen.detail.image.noAssets": "No images on this brand yet. Upload via Brand Studio → Examples.",
  "gen.detail.image.clear": "Remove image",
  "gen.detail.regenerate.cta": "Try another design",
  "gen.detail.regenerate.title": "New attempt for {platform}",
  "gen.detail.regenerate.placeholder": "Optional note: \"make it warmer\", \"less corporate\"…",
  "gen.detail.regenerate.submit": "Regenerate",

  // Calendar view (v0.3.9)
  "calendar.cta": "Calendar",
  "calendar.title": "Content calendar",
  "calendar.prev": "Prev",
  "calendar.next": "Next",
  "calendar.legend.published": "Published",
  "calendar.legend.scheduled": "Scheduled",
  "calendar.legend.draft": "Draft",
  "calendar.legend.stub": "Pending draft",
  "calendar.legend.failed": "Failed",
  "calendar.empty.title": "No scheduled content for this brand yet.",
  "calendar.empty.hint": "Generate new content or use bulk generation to fill the calendar.",

  // Comments inbox (v0.4.0)
  "inbox.eyebrow": "YORUMLAR · COMMENTS",
  "inbox.title": "Comments inbox",
  "inbox.pickBrand": "Pick a brand to review its comments.",
  "inbox.empty": "No comments match this filter.",
  "inbox.pickHint": "Select a comment from the left panel.",
  "inbox.category": "Category",
  "inbox.hide": "Hide",
  "inbox.escalate": "Escalate",
  "inbox.tab.all": "All",
  "inbox.tab.new": "New",
  "inbox.tab.draftPending": "Draft ready",
  "inbox.tab.escalated": "Escalated",
  "inbox.status.new": "New",
  "inbox.status.classifying": "Classifying",
  "inbox.status.draft_pending": "Draft ready",
  "inbox.status.approved": "Approved",
  "inbox.status.sent": "Sent",
  "inbox.status.hidden": "Hidden",
  "inbox.status.escalated": "Escalated",
  "inbox.drafts.title": "AI reply drafts",
  "inbox.drafts.empty": "No drafts yet. Click \"Generate reply\" to start.",
  "inbox.drafts.generate": "Generate reply",
  "inbox.drafts.edit": "Edit",
  "inbox.drafts.approve": "Approve",
  "inbox.drafts.cancel": "Cancel",
  "inbox.drafts.save": "Save",
  "inbox.drafts.sent": "sent",
  "inbox.drafts.approved": "approved",
  "gen.detail.cost": "Cost",
  "gen.detail.latency": "Latency",
  "gen.detail.model": "Model",
  "gen.detail.notReady": "Generation must be 'ready' to approve.",
  "gen.detail.approvedNote": "This generation has been approved. Draft posts were created from the versions below.",
  "gen.field.variants": "Variants per platform",
  "gen.field.variantsHint": "How many distinct drafts per platform. More variants = more cost.",
  "gen.field.references": "Reference examples",
  "gen.field.referencesHint": "Pick up to 5. The AI uses them as visual + tonal references.",
  "gen.field.referencesEmpty": "No example posts uploaded for this brand yet.",
  "gen.field.referencesEmptyCTA": "Open Brand Studio →",

  "post.detail.title": "Post detail",
  "post.detail.backToGallery": "Back to gallery",
  "post.detail.body": "Body",
  "post.detail.hashtags": "Hashtags",
  "post.detail.image": "Image",
  "post.detail.edit": "Edit",
  "post.detail.cancel": "Cancel",
  "post.detail.save": "Save",
  "post.detail.schedule": "Schedule",
  "post.detail.scheduleAt": "Publish at",
  "post.detail.scheduleHint": "Must be at least 1 minute in the future.",
  "post.detail.scheduleSubmit": "Schedule",
  "post.detail.cancelPost": "Cancel post",
  "post.detail.deletePost": "Delete post",
  "post.detail.deleteConfirm.title": "Delete this post?",
  "post.detail.deleteConfirm.body": "Deleted posts cannot be recovered.",
  "post.detail.scheduledFor": "Scheduled for",
  "post.detail.publishedAt": "Published at",
  "post.detail.notEditable": "This post is not editable (only draft/scheduled posts).",
  "post.detail.aiDisclosure": "Show AI label",
  "post.detail.publishTelegram": "Publish to Telegram",
  "post.detail.publishTelegramSubtitle": "Publish this post now to the brand's Telegram channel via the shared bot (@nexus_dijital_bot).",
  "post.detail.publishNow": "Publish now",
  "post.detail.publishedTitle": "Published on Telegram",
  "post.detail.failedTitle": "Publish failed",
  "post.detail.status": "Status",
  "post.detail.dangerZone": "Danger zone",
  "post.detail.reschedule": "Reschedule",
  "post.detail.cancelConfirm.title": "Cancel this post?",
  "post.detail.cancelConfirm.body": "The post will move back to draft and won't be published. You can reschedule it later.",
  "brands.kit.importTitle": "Import a brand kit",
  "brands.kit.importSubtitle": "Upload an HTML brand kit. We extract colors, fonts, voice, logo, and post templates. You pick which pieces to apply on the next screen.",
  "brands.kit.importButton": "Choose HTML file",
  "brands.kit.importReviewTitle": "Review what to import",
  "brands.kit.importReviewSubtitle": "Pick which items to apply. Nothing is auto-saved — after applying you'll see the merged kit in the editor below; click Save to persist.",
  "brands.kit.importApply": "Apply selected",
  "brands.kit.importSectionPalette": "Color palette",
  "brands.kit.importSectionTypography": "Typography",
  "brands.kit.importSectionVoice": "Brand voice",
  "brands.kit.importSectionLogo": "Logo",
  "brands.kit.importSectionTemplates": "Post templates",
  "brands.kit.importNone": "Nothing to import in this section.",
  "brands.aiAssist.title": "AI brand setup",
  "brands.aiAssist.badge": "AI",
  "brands.aiAssist.subtitle": "Describe your business in one paragraph and AI will draft your industry, brand voice, target audience, products, services, and content topics. You review every item before anything is applied.",
  "brands.aiAssist.openButton": "Let AI help",
  "brands.aiAssist.descTitle": "Tell us about your business",
  "brands.aiAssist.descSubtitle": "Describe what you do, who it's for, and what makes you different. The clearer you are, the better the draft.",
  "brands.aiAssist.descLabel": "Description",
  "brands.aiAssist.descPlaceholder": "Example: I run a handmade ceramic studio in Kadıköy since 2019. Each piece is one-of-a-kind, using eco-friendly glazes. My customers are design-conscious women aged 25-40 who want unique decor and meaningful gifts...",
  "brands.aiAssist.descHint": "At least one or two sentences. AI uses this to draft industry, voice, audience, and content ideas.",
  "brands.aiAssist.generate": "Generate draft",
  "brands.aiAssist.errorTooShort": "Please describe the business in at least one sentence (20+ characters).",
  "brands.aiAssist.reviewTitle": "Review the draft",
  "brands.aiAssist.reviewSubtitle": "Pick which sections to apply. Checked items will be saved into the matching brand panels.",
  "brands.aiAssist.apply": "Apply selected",
  "brands.aiAssist.sectionGeneral": "Industry & category",
  "brands.aiAssist.sectionVoice": "Brand voice",
  "brands.aiAssist.sectionAudience": "Target audience",
  "brands.aiAssist.sectionProducts": "Products",
  "brands.aiAssist.sectionServices": "Services",
  "brands.aiAssist.sectionTopics": "Content topics",
  "brands.aiAssist.setupTitle": "Set up your brand with AI",
  "brands.aiAssist.setupSubtitle": "Fill in whatever you have. Everything is optional — the description alone is enough. AI processes them together and shows you one review screen.",
  "brands.aiAssist.errorEmpty": "Please fill in at least one input (description, brand kit, logo, or sample posts).",
  "brands.aiAssist.statusWorking": "Processing...",
  "brands.aiAssist.kitLabel": "Upload brand kit (HTML)",
  "brands.aiAssist.kitHint": "If you have an HTML brand kit, we'll extract colors, fonts, voice and templates from it.",
  "brands.aiAssist.kitChoose": "Choose HTML file",
  "brands.aiAssist.kitReplace": "Replace",
  "brands.aiAssist.logoLabel": "Upload logo",
  "brands.aiAssist.logoHint": "PNG, JPG or WebP. The logo is uploaded directly to brand assets.",
  "brands.aiAssist.logoChoose": "Choose logo",
  "brands.aiAssist.logoReplace": "Replace",
  "brands.aiAssist.samplesLabel": "Sample posts",
  "brands.aiAssist.samplesHint": "Add 1-10 images so AI learns your brand's visual style. They're used as references during content generation.",
  "brands.aiAssist.samplesChoose": "Choose images",
  "brands.aiAssist.samplesCount": "images chosen",
  "brands.aiAssist.remove": "Remove",
  "brands.aiAssist.logoUploaded": "Logo uploaded ✓",
  "brands.aiAssist.samplesUploaded": "Sample posts uploaded",
  "brands.aiAssist.logoVariantsTitle": "Logo variants",
  "brands.aiAssist.logoVariantsHint": "Pick which version is your primary logo. Other variants are kept as references for AI image generation (dark/light/colored-surface treatments).",
  "brands.aiAssist.logoPrimary": "Primary",
  "brands.aiAssist.logoMakePrimary": "Make primary",
  "brands.aiAssist.logoInclude": "Include",
  "app.nav.primary": "Main navigation",
  "app.nav.openMenu": "Open menu",
  "app.nav.thisBrand": "This brand",
  "app.nav.calendar": "Calendar",
  "app.nav.comments": "Comments",
  "app.nav.gallery": "Gallery",
  "app.nav.sectionMain": "Main",
  "app.nav.sectionAccount": "Account",
  "app.nav.search": "Search",
  "app.nav.notifications": "Notifications",
  "app.nav.signedInAs": "Signed in as",
  "gen.detail.platformsLabel": "platforms",
  "gen.detail.variantSingular": "variant",
  "gen.detail.variantPlural": "variants",
  "gen.detail.progressLabel": "Progress",
  "gen.detail.readyToApprove": "Ready to approve ✓",
  "gen.detail.debugPrompt": "Show actual prompt sent to model",
};

const dicts: Record<Locale, Dict> = { tr, en };

type I18nContextValue = {
  locale: Locale;
  /** Set explicitly by user (button click). Persists + syncs to server. */
  setLocale: (l: Locale) => void;
  /** Adopt the server's locale on sign-in. No-op if the user has already
   *  made an explicit pick — their choice wins. */
  adoptServerLocale: (l: string) => void;
  t: (key: string, vars?: Record<string, string>) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function normalize(s: string | null | undefined): Locale | null {
  if (!s) return null;
  const lower = s.toLowerCase();
  if (lower === "tr" || lower.startsWith("tr-")) return "tr";
  if (lower === "en" || lower.startsWith("en-")) return "en";
  return null;
}

/** Synchronous initial pick:
 *    shared cookie > localStorage > navigator.language > "tr". */
function bootLocale(): { locale: Locale; source: Source } {
  if (typeof window === "undefined") return { locale: "tr", source: "boot" };
  // Shared cross-subdomain pick wins over the per-subdomain localStorage —
  // landing may have set it, dashboard should respect that.
  const cookie = normalize(readCookie(SHARED_COOKIE));
  if (cookie) return { locale: cookie, source: "user" };
  const saved = normalize(localStorage.getItem(STORAGE_KEY));
  if (saved) return { locale: saved, source: "user" };
  const nav = normalize(navigator.language);
  return { locale: nav ?? "tr", source: "boot" };
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const init = useMemo(bootLocale, []);
  const [locale, setLocaleState] = useState<Locale>(init.locale);
  const sourceRef = useRef<Source>(init.source);

  useEffect(() => {
    document.documentElement.lang = locale === "tr" ? "tr" : "en";
  }, [locale]);

  // Async tier 3: /v1/geo. Only adopts if the user hasn't made an explicit
  // pick. boot-default = "not picked yet" so geo can override it.
  useEffect(() => {
    if (sourceRef.current === "user" || sourceRef.current === "server") return;
    let cancelled = false;
    geo.get()
      .then((g) => {
        if (cancelled) return;
        if (sourceRef.current === "user" || sourceRef.current === "server") return;
        const sug = normalize(g.suggested_locale);
        if (!sug) return;
        // Don't write the shared cookie from geo — cookie should only
        // reflect explicit picks. Geo just nudges the in-memory state and
        // localStorage so a same-subdomain refresh stays consistent.
        localStorage.setItem(STORAGE_KEY, sug);
        if (sug !== locale) setLocaleState(sug);
      })
      .catch(() => {
        // best-effort; silent failure is fine
      });
    return () => {
      cancelled = true;
    };
    // run-once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLocale = useCallback((l: Locale) => {
    sourceRef.current = "user";
    // Cross-subdomain sharing (landing + dashboard see the same value).
    writeSharedCookie(SHARED_COOKIE, l);
    // Per-subdomain fast read (no cookie parsing on every page load).
    localStorage.setItem(STORAGE_KEY, l);
    setLocaleState(l);
    // Server sync; only succeeds if signed in. We don't undo the UI on
    // failure — a 401 is an auth-layer problem, not a UI problem.
    auth.patchMe({ locale: l }).catch(() => {});
  }, []);

  const adoptServerLocale = useCallback((serverLocale: string) => {
    const norm = normalize(serverLocale);
    if (!norm) return;
    if (sourceRef.current === "user") return;
    sourceRef.current = "server";
    // Mirror the server's value into the shared cookie + localStorage so
    // the landing page picks up the user's stored profile pref too.
    writeSharedCookie(SHARED_COOKIE, norm);
    localStorage.setItem(STORAGE_KEY, norm);
    setLocaleState(norm);
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      adoptServerLocale,
      t: (key, vars) => {
        let s = dicts[locale][key] ?? key;
        if (vars) for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, v);
        return s;
      },
    }),
    [locale, setLocale, adoptServerLocale],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const v = useContext(I18nContext);
  if (!v) throw new Error("useI18n must be used inside <I18nProvider>");
  return v;
}
